import { useEffect, useState } from 'react';
import axios from 'axios';

const FIELD_DEFS = [
  { key: 'company',       label: 'Company',          required: true },
  { key: 'site_number',   label: 'Site Number' },
  { key: 'address_full',  label: 'Address',          wide: true },
  { key: 'contact_name',  label: 'Contact Name' },
  { key: 'phone_main',    label: 'Phone Main' },
  { key: 'email1',        label: 'Email',            required: true, type: 'email' },
  { key: 'pos_type',      label: 'POS Type' },
  { key: 'date',          label: 'Quote Date',       type: 'date' },
  // Multi-site quoting. For a chain lead (number_in_chain > 1) this
  // prefills with the chain size; rep can override. line_total in the
  // quote PDF scales by qty automatically.
  { key: 'qty',           label: 'Quantity (sites)' },
  // Payment terms — editable per-deal. Defaults to Net 30 if blank
  // (server-side default in quotesController).
  { key: 'payment_terms', label: 'Payment Terms',    wide: true }
];

function todayISO() { return new Date().toISOString().slice(0, 10); }

// Sticky-form persistence. After each successful Generate Quote, the
// three free-text fields (payment terms, discount description, discount
// amount) are saved to localStorage so the next quote opens with the
// same values pre-filled. Reduces re-typing for reps who use a
// consistent payment-terms phrase across most quotes but occasionally
// override for a specific deal.
//
// Per-browser only — if Dave uses a second machine the defaults won't
// carry over. Acceptable trade-off vs persisting server-side.
const STICKY_KEY = 'xsite_quote_sticky_fields_v1';
const STICKY_KEYS = ['payment_terms', 'discount_description', 'discount_amount'];

function loadStickyFields() {
  try {
    const raw = typeof localStorage !== 'undefined' && localStorage.getItem(STICKY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Whitelist allowed keys — refuse anything else in case the stored
    // payload has extra fields from an older schema.
    const out = {};
    for (const k of STICKY_KEYS) {
      if (typeof parsed[k] === 'string') out[k] = parsed[k];
    }
    return out;
  } catch (_) {
    return {};
  }
}

function saveStickyFields(fields) {
  try {
    if (typeof localStorage === 'undefined') return;
    const payload = {};
    for (const k of STICKY_KEYS) {
      if (fields[k] != null) payload[k] = String(fields[k]);
    }
    localStorage.setItem(STICKY_KEY, JSON.stringify(payload));
  } catch (_) { /* best effort */ }
}

// Build the prefill from a customer (billing entity) and optionally a
// site (operational entity). Operational fields — address, site
// number, POS type, fuel brand — come from the site when present; the
// billing contact (company, contact name, phone, email) stays on the
// customer.
// Quote currency default: Canadian provinces -> CAD, any other (US) state -> USD.
// The rep can flip it with the toggle in the dialog.
const CA_PROVINCES = new Set(['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT']);
function defaultCurrency(record, site) {
  const st = String((site && site.province_state) || record.province_state || '').trim().toUpperCase();
  if (!st) return 'CAD';
  return CA_PROVINCES.has(st) ? 'CAD' : 'USD';
}

function prefillFromRecord(record, site /* optional */) {
  const sourceForOps = site && Object.keys(site).length > 0 ? site : record;
  const cityProvince = [sourceForOps.city, sourceForOps.province_state].filter(Boolean).join(', ');
  const addressFull = [
    sourceForOps.address1 || sourceForOps.address,    // sites use `address`, customers use `address1`
    cityProvince,
    sourceForOps.postal_zip
  ].filter(Boolean).join(', ').replace(/, $/, '');
  const contactName = [record.first_name, record.last_name].filter(Boolean).join(' ');
  // Fuel brand also tracks the site when one is in play (a chain can
  // carry mixed brands across locations). Falls back to the customer
  // record when the site row doesn't have a fuel_brand set.
  const fuelBrand = (site && site.fuel_brand) || record.fuel_brand || '';
  const discountDescription = fuelBrand
    ? `${fuelBrand} dealer discount`
    : '';
  const discountAmount = fuelBrand ? '450' : '';
  // Site phone (if recorded) wins over the customer's main phone when
  // generating a quote for a specific location — that's the on-site
  // contact David would point a customer to.
  const phone = (site && site.site_phone) || record.phone_main || '';
  return {
    company:       record.company || '',
    site_number:   (site && site.site_number) || record.site_number || '',
    address_full:  addressFull,
    contact_name:  contactName,
    phone_main:    phone,
    email1:        record.email1 || '',
    pos_type:      (site && site.pos_type) || record.pos_type || '',
    date:          todayISO(),
    discount_description: discountDescription,
    discount_amount:      discountAmount,
    // Default qty from the chain size if known (lead.number_in_chain
    // or customer.number_in_chain). Falls back to '1' for single-site
    // records. Stored as a string because the input is a text field.
    qty: String(record.number_in_chain || 1),
    payment_terms: 'Net 30 days from invoice',
    currency: defaultCurrency(record, site)
  };
}

// Where test-mode sends are redirected. Kept in sync with TEST_RECIPIENT
// on the server — purely informational here for the dialog's UI text.
const TEST_DESTINATION = 'david@xsitegroup.ca';

export default function QuoteDialog({ open, record, site, onClose }) {
  // step: 'form' | 'generated' | 'sent'
  const [step, setStep] = useState('form');
  const [fields, setFields] = useState(() => ({ ...prefillFromRecord(record || {}, site), ...loadStickyFields() }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(null);  // { document_id, file_path, filename, opened_in_word }
  const [sent, setSent] = useState(null);            // { sent_at, resend_message_id, subject }
  // Test Mode defaults to ON — failsafe so a real client never gets a
  // quote accidentally during development. User must explicitly disable.
  const [testMode, setTestMode] = useState(true);
  // Word doc preview/edit flow (production path — "open in Word" only
  // works on the Windows dev box).
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [editedDocx, setEditedDocx] = useState(null);  // File from <input type="file">
  // One-off / custom charge lines typed into this quote (e.g. first-time
  // data import). Each: { description, amount }. Not sticky — cleared per quote.
  const [customLines, setCustomLines] = useState([]);



  useEffect(() => {
    if (open) {
      setStep('form');
      setFields({ ...prefillFromRecord(record || {}, site), ...loadStickyFields() });
      setError(null);
      setGenerated(null);
      setSent(null);
      setBusy(false);
      setTestMode(true);
      setDownloadingDocx(false);
      setEditedDocx(null);
      setCustomLines([]);
    }
  }, [open, record, site]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, busy]);

  if (!open) return null;

  function update(key) {
    return (e) => setFields((prev) => ({ ...prev, [key]: e.target.value }));
  }

  // Custom line-item helpers.
  function addCustomLine() {
    setCustomLines((prev) => [...prev, { description: '', amount: '' }]);
  }
  function updateCustomLine(i, key, value) {
    setCustomLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)));
  }
  function removeCustomLine(i) {
    setCustomLines((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function handleGenerate(e) {
    e.preventDefault();
    setError(null);
    if (!fields.company.trim()) return setError('Company is required.');
    if (!fields.email1.trim()) return setError('Email is required (used when you click Send Quote).');
    setBusy(true);
    try {
      // site_id is included when the dialog was opened from a specific
      // site so the server can use the site's subscription for line
      // items + the site's province for tax. contact_id stays as the
      // customer's id (the billing entity).
      const body = { contact_id: record.id, fields };
      if (site && site.id) body.site_id = site.id;
      // Only send custom lines that have both a description and a positive amount.
      const validLines = customLines
        .map((l) => ({ description: String(l.description || '').trim(), amount: l.amount }))
        .filter((l) => l.description && Number(l.amount) > 0);
      if (validLines.length) body.custom_lines = validLines;
      const res = await axios.post('/api/quotes/generate', body);
      // Persist the sticky fields so the next Generate Quote dialog
      // opens with the same payment terms / discount values pre-filled.
      // Happens only after a successful generate — failed attempts
      // don't pollute the saved defaults.
      saveStickyFields(fields);
      setGenerated(res.data);
      setStep('generated');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handlePreviewDocx() {
    if (!generated?.document_id) return;
    setDownloadingDocx(true);
    setError(null);
    try {
      const res = await axios.get(`/api/quotes/${generated.document_id}/docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = generated.filename || `quote-${generated.document_id}.docx`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setDownloadingDocx(false);
    }
  }

  function handleDocxFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) { setEditedDocx(null); return; }
    if (!f.name.toLowerCase().endsWith('.docx')) {
      setError('Please pick a .docx file');
      e.target.value = '';
      return;
    }
    setEditedDocx(f);
    setError(null);
  }

  async function handleSend() {
    if (!generated) return;
    setError(null);
    setBusy(true);
    try {
      let res;
      if (editedDocx) {
        // Multipart so the edited .docx rides along. to/company/test_mode
        // are still required by the server — send them as form fields.
        const fd = new FormData();
        fd.append('docx', editedDocx);
        fd.append('to', fields.email1);
        // CC is optional -- only append when non-empty (FormData
        // doesn't accept undefined the way JSON does).
        if (fields.cc && fields.cc.trim()) fd.append('cc', fields.cc.trim());
        fd.append('company', fields.company);
        fd.append('test_mode', String(testMode));
        res = await axios.post(`/api/quotes/${generated.document_id}/send`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await axios.post(`/api/quotes/${generated.document_id}/send`, {
          to: fields.email1,
          cc: (fields.cc || '').trim() || undefined,
          company: fields.company,
          test_mode: testMode,
        });
      }
      setSent(res.data);
      setStep('sent');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !busy) onClose();
    }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="Generate Quote">
        <header className="modal-head">
          <h3>
            {step === 'form' && 'Generate Quote'}
            {step === 'generated' && 'Review & Send Quote'}
            {step === 'sent' && 'Quote Sent'}
          </h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </header>

        {step === 'form' && (
          <form className="modal-body" onSubmit={handleGenerate}>
            <p className="modal-sub">
              Review the merge fields. Clicking Generate will save a Word document to
              <code> /documents/quotes/</code> and open it in Word for review.
            </p>
            {error && <p className="error">{error}</p>}
            <div className="form-field" style={{ marginBottom: 'var(--space-3)', maxWidth: 240 }}>
              <span>Currency</span>
              <div style={{ display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden', width: 'fit-content' }}>
                {['CAD', 'USD'].map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={fields.currency === c ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                    style={{ borderRadius: 0 }}
                    onClick={() => setFields((f) => ({ ...f, currency: c }))}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-grid">
              {FIELD_DEFS.map((f) => (
                <label key={f.key} className={`form-field${f.wide ? ' form-field-wide' : ''}`}>
                  <span>
                    {f.label}
                    {f.required && <span className="req"> *</span>}
                  </span>
                  <input
                    type={f.type || 'text'}
                    value={fields[f.key]}
                    onChange={update(f.key)}
                    required={f.required}
                  />
                </label>
              ))}
            </div>

            {/* Discount — appears at the bottom of the quote's line items.
                Description prefills from the contact's fuel brand; amount
                defaults to 450. Clear either to skip the discount line
                entirely. */}
            <h4 className="preview-heading" style={{ marginTop: 'var(--space-4)' }}>
              Discount
            </h4>
            <p className="modal-sub" style={{ marginTop: 0 }}>
              Auto-populated from the customer's fuel brand. Edit the
              description or change the amount before generating. Leave
              the amount blank or zero for no discount.
            </p>
            <div className="form-grid">
              <label className="form-field form-field-wide">
                <span>Discount Description</span>
                <input
                  type="text"
                  value={fields.discount_description}
                  onChange={update('discount_description')}
                  placeholder="e.g. Esso dealer discount"
                />
              </label>
              <label className="form-field">
                <span>Discount Amount ($)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={fields.discount_amount}
                  onChange={update('discount_amount')}
                  placeholder="0.00"
                  className="cell-mono"
                />
              </label>
            </div>

            {/* Additional / one-off charge lines — setup, first-time data
                import, hardware, etc. Each row is added to the quote's
                line items and the subtotal (tax applies). Add as many as
                needed; blank rows are ignored. */}
            <h4 className="preview-heading" style={{ marginTop: 'var(--space-4)' }}>
              Additional Line Items
            </h4>
            <p className="modal-sub" style={{ marginTop: 0 }}>
              One-off charges for this quote (e.g. “Server Setup, Program
              Configuration and Site Data First Time Import”). Leave empty if none.
            </p>
            {customLines.map((line, i) => (
              <div
                key={i}
                style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 8, flexWrap: 'wrap' }}
              >
                <label className="form-field" style={{ flex: '1 1 320px' }}>
                  <span>Description</span>
                  <input
                    type="text"
                    value={line.description}
                    onChange={(e) => updateCustomLine(i, 'description', e.target.value)}
                    placeholder="e.g. Server Setup, Program Configuration and Site Data First Time Import"
                  />
                </label>
                <label className="form-field" style={{ flex: '0 0 140px' }}>
                  <span>Amount ($)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={line.amount}
                    onChange={(e) => updateCustomLine(i, 'amount', e.target.value)}
                    placeholder="0.00"
                    className="cell-mono"
                  />
                </label>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => removeCustomLine(i)}
                  title="Remove this line"
                  style={{ color: 'var(--color-bad, #b91c1c)' }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="btn-secondary btn-sm" onClick={addCustomLine} style={{ marginTop: 4 }}>
              + Add line item
            </button>

            <div className="form-actions" style={{ marginTop: 16 }}>
              <span className="form-hint">
                Saved to <code>/documents/quotes/</code> as
                {' '}<strong>Country_Province_POS_Company_Site_QUOTE_Date.docx</strong>
              </span>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={busy}>
                {busy ? 'Generating…' : 'Generate Quote'}
              </button>
            </div>
          </form>
        )}

        {step === 'generated' && generated && (
          <div className="modal-body">
            <div className="quote-step-card">
              <div className="quote-step-icon quote-step-icon-info">📄</div>
              <div>
                <h4>Word document ready for review</h4>
                <p>
                  {generated.opened_in_word
                    ? 'The file has been opened in Word. Edit as needed and save your changes.'
                    : 'The file was saved but could not be opened automatically. Open it manually from the path below.'}
                </p>
                <dl className="quote-success-meta">
                  {generated.quote_number && (
                    <div><dt>Quote #</dt><dd className="cell-mono">{generated.quote_number}</dd></div>
                  )}
                  <div><dt>File</dt><dd className="cell-mono">{generated.file_path}</dd></div>
                  {generated.archive_docx_path && (
                    <div><dt>Archive</dt><dd className="cell-mono">{generated.archive_docx_path}</dd></div>
                  )}
                  <div><dt>Recipient</dt><dd>{fields.email1}</dd></div>
                  <div><dt>Subject</dt><dd>XSite BackOffice Quote - {fields.company}</dd></div>
                </dl>
              </div>
            </div>

            {error && <p className="error">{error}</p>}

            <p className="modal-sub">
              When you're done editing in Word and have saved the file, click <strong>Send Quote</strong>.
              The current .docx on disk will be converted to PDF and emailed.
            </p>

            <div className="docx-preview-panel">
              <div className="docx-preview-head">
                <h4 style={{ margin: 0 }}>Review Word document</h4>
                <p className="modal-sub" style={{ margin: 0 }}>
                  Download the .docx, edit it in Word if you need to change wording or
                  add line items, then upload the edited version before sending. If you
                  skip this, the generated .docx will be sent as-is.
                </p>
              </div>

              <div className="docx-preview-actions">
                <button type="button" className="btn-secondary" onClick={handlePreviewDocx} disabled={busy || downloadingDocx}>
                  {downloadingDocx ? 'Downloading…' : 'Download .docx'}
                </button>

                <label className="docx-preview-upload">
                  <span>Upload edited:</span>
                  <input type="file" accept=".docx" onChange={handleDocxFileChange} disabled={busy} />
                </label>

                {editedDocx && (
                  <span className="docx-preview-attached">
                    Will send: <strong>{editedDocx.name}</strong>
                    <button type="button" className="btn-link" onClick={() => setEditedDocx(null)}>clear</button>
                  </span>
                )}
              </div>
            </div>

            {/* CC field (Dave 2026-06-26) -- comma- or semicolon-
                separated. Server merges with the David<->Mary CC
                counterpart and dedupes. Test Mode strips it so test
                sends never reach real third parties. */}
            <div style={{ margin: '12px 0' }}>
              <label className="form-field form-field-wide">
                <span>CC</span>
                <input
                  type="text"
                  value={fields.cc || ''}
                  onChange={update('cc')}
                  className="cell-mono"
                  placeholder="optional@example.com, another@example.com"
                  disabled={busy}
                />
              </label>
            </div>

            <div className={`test-mode-panel test-mode-${testMode ? 'on' : 'off'}`}>
              <label className="test-mode-toggle">
                <input
                  type="checkbox"
                  checked={testMode}
                  onChange={(e) => setTestMode(e.target.checked)}
                  disabled={busy}
                />
                <span className="test-mode-label">Test Mode</span>
              </label>
              {testMode ? (
                <p className="test-mode-status">
                  Quote will be redirected to <strong>{TEST_DESTINATION}</strong> instead of
                  <code> {fields.email1}</code>. No note logged on the customer record.
                </p>
              ) : (
                <p className="test-mode-status">
                  <strong>LIVE</strong> — quote will be emailed to the real client at
                  <code> {fields.email1}</code> and a note will be logged on the record.
                </p>
              )}
            </div>

            <div className="form-actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                Close (send later from Documents)
              </button>
              <button type="button" className="btn-primary" onClick={handleSend} disabled={busy}>
                {busy
                  ? 'Converting & sending…'
                  : editedDocx
                    ? (testMode ? 'Send Edited (Test)' : 'Send Edited Quote (LIVE)')
                    : (testMode ? 'Send Test Quote' : 'Send Quote (LIVE)')}
              </button>
            </div>
          </div>
        )}

        {step === 'sent' && sent && (
          <div className="modal-body">
            {sent.test_mode && (
              <p className="error" style={{ background: 'var(--color-warn-bg)', color: 'var(--color-warn)', borderColor: 'var(--color-warn-border)' }}>
                <strong>TEST MODE active</strong> — server redirected the email
                from <code>{sent.intended_to}</code> to <code>{sent.to}</code>.
                No note was logged on the customer record.
              </p>
            )}
            <div className="quote-success">
              <div className="quote-success-icon">✓</div>
              <div className="quote-success-icon">✓</div>
              <h4>{sent.test_mode ? 'Test Quote Sent' : 'Quote Sent'}</h4>
              <p>The PDF has been emailed to <strong>{sent.to}</strong>.</p>
              <dl className="quote-success-meta">
                <div><dt>Subject</dt><dd>{sent.subject}</dd></div>
                <div><dt>Resend ID</dt><dd className="cell-mono">{sent.resend_message_id || '—'}</dd></div>
                <div><dt>Source File</dt><dd className="cell-mono">{sent.file_path}</dd></div>
                {sent.archive_pdf_path && (
                  <div><dt>PDF Archive</dt><dd className="cell-mono">{sent.archive_pdf_path}</dd></div>
                )}
                <div><dt>Sent At</dt><dd className="cell-mono">{new Date(sent.sent_at).toLocaleString()}</dd></div>
              </dl>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={onClose}>Done</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
